import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Table, Spinner } from "react-bootstrap";
import { Tenant } from "./SettingsCustomerPage";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "store/configureStore";
import { getTenantUsersFull, removeTenantUser, updateTenantUserFull } from "store/admin/tenantWideData";
import TenantUser, { UserType } from "entities/TenantUser";
import TenantUserFormModal, { TenantUserFormData } from "./TenantUserFormModal";
import { useConfirmDialog } from "pages/UI/ConfirmDialogComponent";

interface TenantUsersModalProps {
    show: boolean;
    onHide: () => void;
    tenant: Tenant | null;
    manageSAUsers: boolean;
}

const TenantUsersModal: React.FC<TenantUsersModalProps> = ({ show, onHide, tenant, manageSAUsers }) => {
  const { t } = useTranslation("parts");
  const dispatch = useDispatch<AppDispatch>();

  const { list: tenantUsers, loading: tenantUsersLoading } = useSelector(
    (state: AppState) => state.admin.tenantWideData.tenantUsers
  );
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [isUpsertModalOpen, setIsUpsertModalOpen] = useState<boolean>(false);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const currentUser = useSelector((state: AppState) => state.users.auth.data);

  useEffect(() => {
    if (show && tenant) {
      dispatch(getTenantUsersFull(tenant.Id, manageSAUsers));
    } 
  }, [dispatch, show, tenant, manageSAUsers]);

  useEffect(() => {
    if (tenantUsers.length > 0 && !tenantUsersLoading) {
      setUsers(tenantUsers);
    }
  }, [tenantUsers, tenantUsersLoading]);

  const askForConfirmation = (
    title: string = t("common:confirmationTitle"),
    message: string = t("parts:agreements.deleteConfirmationModalTitle")
  ): Promise<boolean> => confirm(title, message);
  
  const handleEditTenantUser = useCallback((tenantUser: TenantUser) => {
    setSelectedUser(tenantUser);
    setIsUpsertModalOpen(true);
  }, [])

  const handleAddTenantUser = () => {
    setSelectedUser(null);
    setIsUpsertModalOpen(true);
  };

  const handleRemoveTenantUser = useCallback(async (tenantUser: TenantUser): Promise<boolean> => {
    const isConfirmed = await askForConfirmation();
    if (!isConfirmed) return false;
    try {
        if (tenantUser.Id && tenant?.Id) {
            dispatch(removeTenantUser(tenant.Id, tenantUser.Id, manageSAUsers));
        }
        return true;
    } catch (error) {
        console.error("Error removing tenant user:", error);
    }
    return false;
  }, [dispatch, tenant]);

  const handleSaveTenantUser = (tenantUserData: TenantUserFormData) => {
    if (tenant) {
        var tenantUser: TenantUser = {
            Id: tenantUserData.Id || "",
            UserName: tenantUserData.UserName,
            Email: tenantUserData.Email,
            Roles: tenantUserData.Roles,
            UserType: tenantUserData.UserType,
            Password: tenantUserData.Password
        };
        dispatch(updateTenantUserFull(tenant.Id, tenantUser, manageSAUsers));
    }
    setIsUpsertModalOpen(false);
  };

      const userTypeOptions = Object.entries(UserType)
        .filter(([, value]) => typeof value === "number")
        .map(([key, value]) => ({
            label: t(`settings.forms.userType.${key}`, key),
            value: value as number
        }));

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{manageSAUsers ? t("settings.globalAdmins") : t("settings.forms.adminUsersForCustomer", { customer: (tenant?.Name || t("settings.forms.customer")) })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {tenantUsersLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" />
                        <p>{t("settings.forms.loadingUsers")}</p>
                    </div>
                ) : tenantUsers.length <= 0 ? (
                    <p>{t("settings.forms.noUsersFound")}</p>
                ) : (
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>{t("approvalWorkflows.values.Name")}</th>
                                <th>{t("approvalWorkflows.values.Email")}</th>
                                <th>{t("settings.forms.userType")}</th>
                                <th>{t("agreements.columns.role")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.Id}>
                                    <td>{user.UserName}</td>
                                    <td>{user.Email}</td>
                                    <td>{userTypeOptions[user.UserType]?.label}</td>
                                    <td>{user.Roles.join(', ')}</td>
                                    <td className="text-center">
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            onClick={() => handleEditTenantUser(user)}>
                                                {t("common:form.buttons.edit")}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="ms-2"
                                            disabled={user.Id === currentUser?.UserGuid}
                                            onClick={() => handleRemoveTenantUser(user)}>
                                                {t("common:form.buttons.delete")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button 
                    variant="primary"
                    onClick={handleAddTenantUser}
                >
                    + {manageSAUsers ? t("settings.addNewSAUser") : t("settings.addNewAdminUser")}
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    {t("agreements.form.close")}
                </Button>
            </Modal.Footer>

            <TenantUserFormModal
              show={isUpsertModalOpen}
              onHide={() => {setIsUpsertModalOpen(false)}}
              onFormSubmit={handleSaveTenantUser}
              initialData={selectedUser}
              createSuperAdmin={manageSAUsers}
            />

            <ConfirmDialogComponent />
        </Modal>
    );
};

export default TenantUsersModal;